import { Component, type ReactNode, type ErrorInfo } from 'react'
import { TriangleAlert, RefreshCw, LayoutDashboard } from 'lucide-react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

function ErrorFallback({ error, onReset }: { error: Error | null; onReset: () => void }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--ink-50)',
        padding: 24,
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-lg)',
          padding: '48px 40px',
          maxWidth: 480,
          width: '100%',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: 'var(--danger-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
          }}
        >
          <TriangleAlert size={26} color="var(--danger)" />
        </div>

        <h1
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: 'var(--ink-950)',
            margin: '0 0 8px',
          }}
        >
          Something went wrong
        </h1>

        <p
          style={{
            fontSize: 14,
            color: 'var(--ink-500)',
            margin: '0 0 24px',
            lineHeight: 1.6,
          }}
        >
          An unexpected error occurred. Try reloading the page — if it keeps happening, contact support.
        </p>

        {error?.message && (
          <p
            style={{
              fontSize: 12,
              color: 'var(--ink-400)',
              background: 'var(--ink-100)',
              borderRadius: 'var(--radius-sm)',
              padding: '8px 12px',
              margin: '0 0 24px',
              fontFamily: 'monospace',
              textAlign: 'left',
              wordBreak: 'break-word',
            }}
          >
            {error.message}
          </p>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button
            onClick={onReset}
            className="btn primary"
            style={{ display: 'flex', alignItems: 'center', gap: 7 }}
          >
            <RefreshCw size={14} />
            Reload page
          </button>
          <a
            href="/"
            className="btn"
            style={{ display: 'flex', alignItems: 'center', gap: 7 }}
          >
            <LayoutDashboard size={14} />
            Go to Dashboard
          </a>
        </div>
      </div>
    </div>
  )
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] Uncaught error:', error, info.componentStack)
  }

  reset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} onReset={this.reset} />
    }
    return this.props.children
  }
}
